import { Injectable } from '@angular/core';
import { logEvent, setUserId, setUserProperties, Analytics } from 'firebase/analytics';
import { ANALYTICS } from '../firebase.config';

@Injectable({
    providedIn: 'root'
})
export class AnalyticsService {
    private analytics: Analytics = ANALYTICS;

    constructor() { }

    /**
     * Log a custom event to Firebase Analytics
     * @param eventName Name of the event
     * @param eventParams Optional parameters for the event
     */
    logEvent(eventName: string, eventParams?: { [key: string]: any }) {
        try {
            logEvent(this.analytics, eventName, eventParams);
            console.debug(`[Analytics] Event logged: ${eventName}`, eventParams);
        } catch (error) {
            console.error('[Analytics] Error logging event:', error);
        }
    }

    /**
     * Set the user ID for cross-device tracking
     * @param userId The unique user ID
     */
    setUserId(userId: string | null) {
        try {
            setUserId(this.analytics, userId);
            console.debug(`[Analytics] User ID set: ${userId}`);
        } catch (error) {
            console.error('[Analytics] Error setting user ID:', error);
        }
    }

    /**
     * Set user properties (e.g., role, plan type)
     * @param properties Object containing user properties
     */
    setUserProperties(properties: { [key: string]: any }) {
        try {
            setUserProperties(this.analytics, properties);
            console.debug('[Analytics] User properties set:', properties);
        } catch (error) {
            console.error('[Analytics] Error setting user properties:', error);
        }
    }

    /**
     * Helper to log page views
     * @param url The URL of the page viewed
     */
    logPageView(url: string) {
        this.logEvent('page_view', {
            page_path: url,
            page_location: window.location.href,
            page_title: document.title
        });
    }

    /**
     * Helper to log login events
     * @param method Login method (e.g., 'email', 'google')
     */
    logLogin(method: string = 'email') {
        this.logEvent('login', { method });
    }

    /**
     * Helper to log loan view events
     * @param loanId The ID of the loan viewed
     */
    logLoanView(loanId: string) {
        this.logEvent('view_item', {
            item_id: loanId,
            item_name: 'loan_deal',
            content_type: 'loan'
        });
    }

    /**
     * Helper to log investment events
     * @param amount Investment amount
     * @param loanId The ID of the loan invested in
     */
    logInvestment(amount: number, loanId: string) {
        this.logEvent('purchase', {
            transaction_id: `inv_${Date.now()}`,
            value: amount,
            currency: 'GBP',
            items: [{
                item_id: loanId,
                item_name: 'loan_investment',
                price: amount,
                quantity: 1
            }]
        });
    }

    // ===== CLIENT PORTAL EVENTS =====

    /**
     * Track when user views the deal list/opportunities dashboard
     */
    logViewDealList() {
        this.logEvent('view_deal_list');
    }

    /**
     * Track when user views a specific deal detail page
     */
    logViewDealDetail(dealId: string, dealName: string) {
        this.logEvent('view_deal_detail', {
            deal_id: dealId,
            deal_name: dealName
        });
    }

    /**
     * Track when user opens the commitment modal
     */
    logBeginCommitment(dealId: string) {
        this.logEvent('begin_commitment', {
            deal_id: dealId
        });
    }

    /**
     * Track when user adds a commitment to their staging list
     */
    logAddCommitmentToCart(amount: number, dealId: string, accountType: string) {
        this.logEvent('add_commitment_to_cart', {
            value: amount,
            currency: 'GBP',
            deal_id: dealId,
            account_type: accountType
        });
    }

    /**
     * Track when user confirms/submits commitments
     */
    logSubmitCommitment(totalAmount: number, dealId: string, numAccounts: number) {
        this.logEvent('submit_commitment', {
            value: totalAmount,
            currency: 'GBP',
            deal_id: dealId,
            num_accounts: numAccounts
        });
    }

    /**
     * Track when user edits an existing commitment
     */
    logEditCommitment(dealId: string, oldAmount: number, newAmount: number) {
        this.logEvent('edit_commitment', {
            deal_id: dealId,
            old_value: oldAmount,
            new_value: newAmount,
            currency: 'GBP'
        });
    }

    /**
     * Track when user deletes a commitment
     */
    logDeleteCommitment(dealId: string, amount: number) {
        this.logEvent('delete_commitment', {
            deal_id: dealId,
            value: amount,
            currency: 'GBP'
        });
    }

    /**
     * Track when user signs Heads of Terms
     */
    logSignHeadsOfTerms(dealId: string, accountType: string) {
        this.logEvent('sign_heads_of_terms', {
            deal_id: dealId,
            account_type: accountType
        });
    }

    /**
     * Track when user views their portfolio
     */
    logViewPortfolio(numDeals: number, totalInvested: number) {
        this.logEvent('view_portfolio', {
            num_deals: numDeals,
            total_invested: totalInvested,
            currency: 'GBP'
        });
    }

    /**
     * Track when user clicks through to a deal from portfolio
     */
    logViewDealFromPortfolio(dealId: string) {
        this.logEvent('view_deal_from_portfolio', {
            deal_id: dealId
        });
    }

    /**
     * Track when user downloads a document
     */
    logDownloadDocument(documentType: string, dealId?: string) {
        this.logEvent('download_document', {
            document_type: documentType,
            ...(dealId && { deal_id: dealId })
        });
    }

    /**
     * Track when user starts KYC form
     */
    logStartKyc() {
        this.logEvent('start_kyc');
    }

    /**
     * Track when user submits KYC form
     */
    logSubmitKyc(clientType: string) {
        this.logEvent('submit_kyc', {
            client_type: clientType
        });
    }

    /**
     * Track when user uploads a KYC document
     */
    logUploadKycDocument(documentType: string) {
        this.logEvent('upload_kyc_document', {
            document_type: documentType
        });
    }

    /**
     * Track when user creates a support ticket
     */
    logCreateTicket(subjectLength: number) {
        this.logEvent('create_ticket', {
            subject_length: subjectLength
        });
    }

    /**
     * Track when user views messages/tickets page
     */
    logViewMessages(numTickets: number) {
        this.logEvent('view_messages', {
            num_tickets: numTickets
        });
    }

    /**
     * Track when user sends a message in a ticket
     */
    logSendMessage(ticketId: string) {
        this.logEvent('send_message', {
            ticket_id: ticketId
        });
    }

    /**
     * Track when user views their profile
     */
    logViewProfile() {
        this.logEvent('view_profile');
    }

    /**
     * Track when user updates their profile
     */
    logUpdateProfile() {
        this.logEvent('update_profile');
    }

    /**
     * Track when user changes email preferences
     */
    logUpdateEmailPreferences() {
        this.logEvent('update_email_preferences');
    }

    /**
     * Track when user toggles theme
     */
    logToggleTheme(newTheme: string) {
        this.logEvent('toggle_theme', {
            theme: newTheme
        });
    }

    /**
     * Track when user changes active tab on deal detail page
     */
    logViewDealTab(dealId: string, tabName: string) {
        this.logEvent('view_deal_tab', {
            deal_id: dealId,
            tab_name: tabName
        });
    }

    // ===== ADMIN PORTAL EVENTS =====

    /**
     * Track when admin creates a new loan deal
     */
    logAdminCreateDeal(dealName: string, loanAmount: number) {
        this.logEvent('admin_create_deal', {
            deal_name: dealName,
            loan_amount: loanAmount,
            currency: 'GBP'
        });
    }

    /**
     * Track when admin updates a deal
     */
    logAdminUpdateDeal(dealId: string, status: string) {
        this.logEvent('admin_update_deal', {
            deal_id: dealId,
            status: status
        });
    }

    /**
     * Track when admin views deal detail
     */
    logAdminViewDeal(dealId: string) {
        this.logEvent('admin_view_deal', {
            deal_id: dealId
        });
    }

    /**
     * Track when admin publishes a draft deal
     */
    logAdminPublishDeal(dealId: string, dealName: string) {
        this.logEvent('admin_publish_deal', {
            deal_id: dealId,
            deal_name: dealName
        });
    }

    /**
     * Track when admin executes disbursement
     */
    logAdminExecuteDisbursement(dealId: string, amount: number) {
        this.logEvent('admin_execute_disbursement', {
            deal_id: dealId,
            amount: amount,
            currency: 'GBP'
        });
    }

    /**
     * Track when admin uploads a deal document
     */
    logAdminAddDealDocument(dealId: string, documentType: string) {
        this.logEvent('admin_add_deal_document', {
            deal_id: dealId,
            document_type: documentType
        });
    }

    /**
     * Track when admin views commitment list
     */
    logAdminViewCommitments(numCommitments: number) {
        this.logEvent('admin_view_commitments', {
            num_commitments: numCommitments
        });
    }

    /**
     * Track when admin confirms a commitment
     */
    logAdminConfirmCommitment(dealId: string, amount: number, clientName: string) {
        this.logEvent('admin_confirm_commitment', {
            deal_id: dealId,
            amount: amount,
            currency: 'GBP',
            client_name: clientName
        });
    }

    /**
     * Track when admin confirms funds received
     */
    logAdminConfirmFunds(dealId: string, amount: number, clientName: string) {
        this.logEvent('admin_confirm_funds', {
            deal_id: dealId,
            amount: amount,
            currency: 'GBP',
            client_name: clientName
        });
    }

    /**
     * Track when admin reallocates a commitment
     */
    logAdminReallocateCommitment(originalAmount: number, numNewAllocations: number) {
        this.logEvent('admin_reallocate_commitment', {
            original_amount: originalAmount,
            num_allocations: numNewAllocations,
            currency: 'GBP'
        });
    }

    /**
     * Track when admin generates Heads of Terms
     */
    logAdminGenerateHot(dealId: string, clientName: string) {
        this.logEvent('admin_generate_hot', {
            deal_id: dealId,
            client_name: clientName
        });
    }

    /**
     * Track when admin creates a new client account
     */
    logAdminCreateClient(clientType: string) {
        this.logEvent('admin_create_client', {
            client_type: clientType
        });
    }

    /**
     * Track when admin views client details
     */
    logAdminViewClient(clientId: string) {
        this.logEvent('admin_view_client', {
            client_id: clientId
        });
    }

    /**
     * Track when admin updates KYC/AML status
     */
    logAdminUpdateKycStatus(clientId: string, kycStatus: string, amlStatus: string) {
        this.logEvent('admin_update_kyc_status', {
            client_id: clientId,
            kyc_status: kycStatus,
            aml_status: amlStatus
        });
    }

    /**
     * Track when admin adds a client investment account
     */
    logAdminAddClientAccount(clientId: string, accountType: string) {
        this.logEvent('admin_add_client_account', {
            client_id: clientId,
            account_type: accountType
        });
    }

    /**
     * Track when admin generates a report
     */
    logAdminGenerateReport(reportType: string, format: string) {
        this.logEvent('admin_generate_report', {
            report_type: reportType,
            format: format
        });
    }

    /**
     * Track when admin views the main dashboard
     */
    logAdminViewDashboard() {
        this.logEvent('admin_view_dashboard');
    }

    /**
     * Track when admin runs reconciliation
     */
    logAdminRunReconciliation(dealId?: string) {
        this.logEvent('admin_run_reconciliation', {
            ...(dealId && { deal_id: dealId })
        });
    }

    /**
     * Track when admin processes a redemption
     */
    logAdminProcessRedemption(dealId: string, amount: number, redemptionType: string) {
        this.logEvent('admin_process_redemption', {
            deal_id: dealId,
            amount: amount,
            currency: 'GBP',
            redemption_type: redemptionType
        });
    }

    /**
     * Track when admin records interest payment
     */
    logAdminRecordInterestPayment(dealId: string, amount: number) {
        this.logEvent('admin_record_interest_payment', {
            deal_id: dealId,
            amount: amount,
            currency: 'GBP'
        });
    }

    /**
     * Track when admin marks a loan as defaulted
     */
    logAdminMarkDefault(dealId: string) {
        this.logEvent('admin_mark_default', {
            deal_id: dealId
        });
    }

    /**
     * Track when admin extends a loan term
     */
    logAdminExtendLoan(dealId: string, newEndDate: string) {
        this.logEvent('admin_extend_loan', {
            deal_id: dealId,
            new_end_date: newEndDate
        });
    }
}
